import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { MatDialog } from '@angular/material/dialog';
import { EMPTY } from 'rxjs';
import { catchError, switchMap } from 'rxjs/operators';
import { environment } from '@lmc-env/environment';
import { LmcDialogComponent } from '@lmc-app/shared/components/dialog/lmc-dialog.component';

@Injectable({
	providedIn: 'root',
})
export class VersionCheckService {
	private currentVersion: string | null = null;
	private notificationShown = false;

	constructor(
		private readonly http: HttpClient,
		private readonly dialog: MatDialog
	) {}

	// Called once on app startup to capture the current build version as baseline
	init(): void {
		if (environment.environment === 'Local') {
			return;
		}
		this.fetchVersion().subscribe({
			next: (version) => {
				this.currentVersion = version;
			},
			error: () => {},
		});
	}

	// Called by versionCheckGuard on every route navigation
	check(): void {
		if (environment.environment === 'Local') {
			return;
		}
		if (this.notificationShown) {
			return;
		}
		this.fetchVersion()
			.pipe(catchError(() => EMPTY))
			.subscribe((version) => {
				if (this.isNewVersion(version)) {
					this.showUpdateNotification();
				}
			});
	}

	private fetchVersion() {
		return this.http
			.get<{ version: string }>(`/version.json?t=${Date.now()}`)
			.pipe(switchMap((res) => [res.version]));
	}

	private isNewVersion(version: string): boolean {
		if (!this.currentVersion) {
			this.currentVersion = version;
			return false;
		}
		return version !== this.currentVersion;
	}

	private showUpdateNotification(): void {
		if (this.notificationShown) {
			return;
		}
		this.notificationShown = true;

		const dialogRef = this.dialog.open(LmcDialogComponent, {
			disableClose: true,
			data: {
				title: 'New Version Available',
				content: 'A new version is available. Please reload to get the latest updates.',
				positiveButtonText: 'Reload',
				negativeButtonText: 'Dismiss',
			},
		});

		dialogRef.afterClosed().subscribe((result) => {
			if (result === true) {
				window.location.reload();
			} else {
				this.notificationShown = false;
			}
		});
	}
}